import ReactGA from "react-ga4";
import { readCookieChoice, writeCookieChoice } from "./cookieConsent";

export const MEASUREMENT_ID = (import.meta.env.VITE_GA_MEASUREMENT_ID || '').trim();
const validMeasurementId = /^G-[A-Z0-9]+$/.test(MEASUREMENT_ID);
let initialized = false;
// Resolve the signed-in account preference before enabling optional analytics.
let accountAnalyticsBlocked: boolean | null = null;

export const hasAnalyticsConsent = (): boolean =>
    accountAnalyticsBlocked === false && readCookieChoice()?.marketingMeasurement === true;

let defaultApplied = false;
const defaultConsent = () => {
    if (defaultApplied) return;
    ReactGA.gtag('consent', 'default', { analytics_storage: 'denied', ad_storage: 'denied',
        ad_user_data: 'denied', ad_personalization: 'denied' });
    defaultApplied = true;
};

export const initGA = () => {
    try {
        defaultConsent();
        if (!validMeasurementId || !hasAnalyticsConsent() || initialized) return;
        ReactGA.initialize(MEASUREMENT_ID, { gtagOptions: { send_page_view: false } });
        initialized = true;
    } catch { /* Optional analytics must never break checkout. */ }
};

export const revokeServerAnalytics = () => {
    void fetch('/api/billing/analytics/revoke', { method: 'POST', credentials: 'same-origin' })
        .catch(() => { /* Retry when the banner mounts or the user logs in. */ });
};

let appliedAnalyticsConsent: boolean | null = null;
export const applyCookieConsent = () => {
    const granted = hasAnalyticsConsent();
    const personalized = accountAnalyticsBlocked === false && readCookieChoice()?.personalizedMarketing === true;
    (window as unknown as Record<string, unknown>)[`ga-disable-${MEASUREMENT_ID}`] = !granted;
    try {
        defaultConsent();
        ReactGA.gtag('consent', 'update', { analytics_storage: granted ? 'granted' : 'denied',
            ad_storage: 'denied', ad_user_data: personalized ? 'granted' : 'denied',
            ad_personalization: personalized ? 'granted' : 'denied' });
        if (granted) initGA();
    } catch { /* Optional telemetry. */ }
    if (!granted) clearAnalyticsCookies();
    if (granted && appliedAnalyticsConsent !== true) trackPageView(window.location.pathname);
    appliedAnalyticsConsent = granted;
};

export const setAccountAnalyticsBlocked = (blocked: boolean | null) => {
    accountAnalyticsBlocked = blocked;
    applyCookieConsent();
    window.dispatchEvent(new Event('cookie-preferences-changed'));
};

const clearAnalyticsCookies = () => {
    try {
        const names = document.cookie.split(';').map(cookie => cookie.split('=')[0].trim())
            .filter(name => /^(_ga($|_)|_gid$|_gat($|_))/.test(name));
        const parts = window.location.hostname.split('.');
        const domains = [''];
        for (let index = 0; index < parts.length - 1; index += 1) {
            const domain = parts.slice(index).join('.');
            domains.push(`; domain=${domain}`, `; domain=.${domain}`);
        }
        for (const name of names) {
            for (const domain of domains) document.cookie = `${name}=; Max-Age=0; path=/${domain}`;
        }
    } catch { /* Optional cookie cleanup must not affect the application. */ }
};

export const setAnalyticsConsent = (granted: boolean) => {
    const choice = readCookieChoice();
    if (choice) writeCookieChoice({ ...choice, marketingMeasurement: granted });
    try { localStorage.setItem('cookieConsent', String(granted)); } catch { /* fail closed */ }
    applyCookieConsent();
    if (!granted) {
        clearAnalyticsCookies();
        revokeServerAnalytics();
    }
};

export interface CheckoutAnalyticsContext {
    analytics_storage: 'granted';
    measurement_id: string;
    client_id: string;
    session_id: string;
}

// Use the existing tag identity before entering Stripe/3DS. Never invent a client
// ID or send purchase from a success page. An ad blocker times out harmlessly.
export const getCheckoutAnalyticsContext = async (): Promise<CheckoutAnalyticsContext | null> => {
    try {
        if (!validMeasurementId || !hasAnalyticsConsent()) return null;
        initGA();
        if (!initialized) return null;
        const get = (field: string) => new Promise<string | null>(resolve => {
            const timer = window.setTimeout(() => resolve(null), 400);
            ReactGA.gtag('get', MEASUREMENT_ID, field, (value: unknown) => {
                window.clearTimeout(timer);
                resolve(typeof value === 'string' || typeof value === 'number' ? String(value) : null);
            });
        });
        const [clientId, sessionId] = await Promise.all([get('client_id'), get('session_id')]);
        if (!clientId || !sessionId || !hasAnalyticsConsent()) return null;
        return { analytics_storage: 'granted', measurement_id: MEASUREMENT_ID, client_id: clientId, session_id: sessionId };
    } catch { return null; }
};

export const trackPageView = (path: string) => {
    if (!initialized || !hasAnalyticsConsent()) return;
    const cleanPath = (path || '/').split('?')[0].split('#')[0] || '/';
    try { ReactGA.send({ hitType: "pageview", page: cleanPath }); } catch { /* Optional telemetry. */ }
};

export const trackEvent = (
    category: string,
    action: string,
    label?: string,
    value?: number
) => {
    if (!initialized || !hasAnalyticsConsent()) return;
    try { ReactGA.event({
        category,
        action,
        label,
        value,
    }); } catch { /* Optional telemetry. */ }
};


export const trackSearch = (searchTerm: string) => {
    // Legal searches can contain personal or confidential facts. Analytics
    // receives only coarse metadata, never the query itself.
    const wordCount = searchTerm.trim().split(/\s+/).filter(Boolean).length;
    const bucket = wordCount <= 3 ? "short" : wordCount <= 20 ? "medium" : "long";
    trackEvent("Search", "Search Submitted", bucket, Math.min(searchTerm.length, 10_000));
};

export const trackDosarSearch = (_dosarNumber: string) => {
    void _dosarNumber;
    trackEvent("Search", "Search Dosar");
};

export const trackViewCase = (caseTitle: string) => {
    trackEvent("Content", "View Case", caseTitle);
};

export const trackSubscribeClick = (planName: string, price: number) => {
    trackEvent("Ecommerce", "Begin Checkout", planName, price);
};

export const trackLoginSuccess = () => {
    trackEvent("Auth", "Login Success");
};

export const trackContactFormSubmit = (subject: string) => {
    trackEvent("Lead", "Contact Form Submit", subject);
};

export const trackAiNavigationStep = (
    nodeId: string,
    status: string,
    fallbackToManual: boolean = false
) => {
    // Only structural node/status codes are sent; never user query text or identifiers.
    const safeLabel = `${String(nodeId).slice(0, 16)}:${String(status).slice(0, 24)}${fallbackToManual ? ':fallback' : ''}`;
    trackEvent("Search", "AI Navigation Step", safeLabel);
};
